import { useState } from 'react';
import { Award, Clock, ShieldCheck, ThumbsUp, TriangleAlert } from 'lucide-react';
import CommentComposer from './CommentComposer';
import type { ForumComment, RejectReason } from '../../../sdk/@types/Forum';

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

/** Mesmos rótulos da fila de moderação — o aluno lê o motivo que o admin marcou. */
export const REJECT_REASON_LABEL: Record<RejectReason, string> = {
  spam: 'Spam ou propaganda',
  offensive: 'Ofensivo',
  offtopic: 'Fora do tema',
  incorrect: 'Informação incorreta',
  other: 'Outro',
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "agora · há 4 min · há 2 h · há 3 d", e data cheia a partir de uma semana.
 *
 * Escrito à mão, e não com `formatDistanceToNowStrict` do date-fns, porque o
 * design abrevia a unidade ("há 2 h", não "há 2 horas") e porque uma função
 * pura é o que os testes conseguem fixar sem congelar o relógio.
 */
export function relativeTime(iso?: string, now: number = Date.now()): string {
  const at = Date.parse(iso ?? '');
  if (Number.isNaN(at)) return '';

  const elapsed = Math.max(0, now - at);
  if (elapsed < MINUTE) return 'agora';
  if (elapsed < HOUR) return `há ${Math.floor(elapsed / MINUTE)} min`;
  if (elapsed < DAY) return `há ${Math.floor(elapsed / HOUR)} h`;
  if (elapsed < 7 * DAY) return `há ${Math.floor(elapsed / DAY)} d`;

  return new Date(at).toLocaleDateString('pt-BR');
}

export function initials(name?: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase();
}

export interface CommentCardProps {
  comment: ForumComment;
  /** Assinatura ativa: sem ela o aluno lê, mas não vota nem responde. */
  canParticipate?: boolean;
  /** Vindo do hook: o aprovado com mais votos da questão. */
  mostUsefulId?: string | null;
  /** 0 = comentário de topo, 1 = resposta. Não existe nível 2. */
  depth?: 0 | 1;
  onVote?: (id: string) => void;
  onFlag?: (id: string) => void;
  onReply?: (parentId: string, body: string) => Promise<unknown> | void;
  onEdit?: (id: string, body: string) => Promise<unknown> | void;
  /** Injetável nos testes para fixar o "há 2 h". */
  now?: number;
}

/**
 * Um comentário do fórum da questão.
 *
 * Renderiza os cinco estados que o design prevê — aprovado, próprio pendente,
 * próprio rejeitado, de staff e "mais útil" — e as respostas de um nível só,
 * recursivamente com `depth`.
 *
 * Cores: o selo de staff e o "Mais útil" usam `accent-purple`/`accent-green`,
 * que são identidade, não semântica (decisão 8 do design); os estados de
 * moderação usam a família `warn`, porque ter o comentário parado na fila ou
 * recusado não é erro do sistema nem ação destrutiva — `danger` fica reservado
 * ao que destrói (§7).
 */
export default function CommentCard({
  comment,
  canParticipate = false,
  mostUsefulId = null,
  depth = 0,
  onVote,
  onFlag,
  onReply,
  onEdit,
  now,
}: CommentCardProps) {
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);

  const approved = comment.status === 'approved';
  const staff = comment.author?.role === 'professor' || comment.author?.role === 'moderador';
  const staffLabel = comment.author?.role === 'professor' ? 'Professor' : 'Moderador';
  const mostUseful = approved && comment.id === mostUsefulId;
  const name = comment.mine ? 'Você' : comment.author?.name ?? 'Aluno';

  // Só respostas públicas — mais as suas próprias, que só você enxerga.
  const replies = (comment.replies ?? []).filter(
    (reply) => reply.status === 'approved' || reply.mine,
  );
  const replyCount = comment.replyCount ?? replies.filter((r) => r.status === 'approved').length;

  const canVote = Boolean(onVote) && canParticipate && approved && !comment.mine;
  const canReply = Boolean(onReply) && canParticipate && approved && depth === 0;
  const canFlag = Boolean(onFlag) && approved && !comment.mine;

  const pending = comment.mine && (comment.status === 'pending' || comment.status === 'flagged');
  const rejected = comment.mine && comment.status === 'rejected';

  const avatarTone = staff
    ? 'bg-accent-purple/[0.14] text-accent-purple-ink'
    : comment.mine
      ? 'bg-surface-hover text-ink-secondary'
      : 'bg-accent-blue/[0.14] text-accent-blue-ink';

  const action =
    'rounded text-xs font-semibold text-ink-secondary transition-colors hover:text-ink ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 ' +
    'focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

  return (
    <li
      data-testid="comment"
      data-comment-id={comment.id}
      data-status={comment.status}
      className={cx(
        'list-none',
        (pending || rejected) && 'rounded-card border border-warn/40 bg-warn/[0.08] p-4',
      )}
    >
      <div className="flex gap-3">
        <span
          aria-hidden
          className={cx(
            'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-xs font-bold',
            avatarTone,
          )}
        >
          {initials(comment.mine ? 'Você' : comment.author?.name)}
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-ink">{name}</span>

            {staff && (
              <span className="inline-flex items-center gap-1 rounded-[5px] bg-accent-purple/[0.14] px-[7px] py-0.5 text-[11px] font-semibold text-accent-purple-ink">
                <ShieldCheck size={12} aria-hidden />
                {staffLabel}
              </span>
            )}

            {mostUseful && (
              <span className="inline-flex items-center gap-1 rounded-[5px] bg-accent-green/[0.16] px-[7px] py-0.5 text-[11px] font-semibold text-accent-green-ink">
                <Award size={12} aria-hidden />
                Mais útil
              </span>
            )}

            {pending && (
              <span className="inline-flex items-center gap-1 rounded-full bg-warn/[0.16] px-[7px] py-0.5 text-[11px] font-semibold text-warn-ink">
                <Clock size={12} aria-hidden />
                Em moderação
              </span>
            )}

            {rejected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-warn/[0.16] px-[7px] py-0.5 text-[11px] font-semibold text-warn-ink">
                <TriangleAlert size={12} aria-hidden />
                Não aprovado
              </span>
            )}

            <span className="text-xs text-ink-muted">
              {relativeTime(comment.createdAt, now)}
              {comment.editedAt ? ' · editado' : ''}
            </span>
          </div>

          {editing ? (
            <CommentComposer
              compact
              canParticipate
              autoFocus
              initialValue={comment.body}
              submitLabel="Salvar"
              placeholder="Edite seu comentário…"
              showModerationNote={!staff}
              onCancel={() => setEditing(false)}
              onSubmit={async (body) => {
                await onEdit?.(comment.id, body);
                setEditing(false);
              }}
            />
          ) : (
            <p className="m-0 whitespace-pre-line text-sm leading-relaxed text-ink-secondary">
              {comment.body}
            </p>
          )}

          {/* O motivo e a mensagem do moderador vêm da API só para o autor. */}
          {rejected && (
            <div className="rounded-input border border-warn/40 bg-warn/[0.1] px-3 py-2 text-xs leading-relaxed text-warn-ink">
              <p className="m-0 font-semibold">
                Comentário não aprovado
                {comment.rejectReason ? ` · ${REJECT_REASON_LABEL[comment.rejectReason]}` : ''}
              </p>
              {comment.moderatorMessage && (
                <p className="m-0 mt-1 text-ink-secondary">{comment.moderatorMessage}</p>
              )}
            </div>
          )}

          {!editing && (
            <div className="flex flex-wrap items-center gap-4 pt-0.5">
              {approved && (
                <button
                  type="button"
                  onClick={canVote ? () => onVote?.(comment.id) : undefined}
                  disabled={!canVote}
                  aria-pressed={comment.voted}
                  aria-label={
                    comment.mine
                      ? 'Você não pode votar no próprio comentário'
                      : `Marcar como útil (${comment.votes})`
                  }
                  className={cx(
                    'inline-flex items-center gap-1.5 rounded text-xs font-semibold transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60',
                    'focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                    comment.voted ? 'text-select-ink' : 'text-ink-secondary',
                    canVote ? 'hover:text-ink' : 'cursor-default opacity-60',
                  )}
                >
                  <ThumbsUp size={14} aria-hidden />
                  <span>{comment.votes}</span>
                  <span className="sr-only">útil</span>
                </button>
              )}

              {canReply && (
                <button type="button" className={action} onClick={() => setReplying((v) => !v)}>
                  Responder
                </button>
              )}

              {comment.mine && onEdit && comment.status !== 'rejected' && (
                <button type="button" className={action} onClick={() => setEditing(true)}>
                  Editar
                </button>
              )}

              {rejected && onEdit && (
                <button type="button" className={action} onClick={() => setEditing(true)}>
                  Editar e reenviar
                </button>
              )}

              {canFlag &&
                (comment.flaggedByMe ? (
                  // Confirmação discreta: o comentário fica onde está até o
                  // moderador decidir, e a ação não volta a ficar clicável.
                  <span role="status" className="text-xs font-medium text-ink-muted">
                    Denúncia enviada
                  </span>
                ) : (
                  <button
                    type="button"
                    className="rounded text-xs font-medium text-ink-muted transition-colors hover:text-ink-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60"
                    onClick={() => onFlag?.(comment.id)}
                  >
                    Denunciar
                  </button>
                ))}

              {depth === 0 && replyCount > 0 && (
                <span className="text-xs text-ink-muted">
                  {replyCount === 1 ? '1 resposta' : `${replyCount} respostas`}
                </span>
              )}
            </div>
          )}

          {replying && (
            <div className="pt-1">
              <CommentComposer
                compact
                autoFocus
                canParticipate
                submitLabel="Responder"
                placeholder="Escreva sua resposta…"
                onCancel={() => setReplying(false)}
                onSubmit={async (body) => {
                  await onReply?.(comment.id, body);
                  setReplying(false);
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Um nível só: responder a uma resposta cria outra resposta do mesmo pai. */}
      {depth === 0 && replies.length > 0 && (
        <ul
          data-testid="comment-replies"
          className="mt-3 flex flex-col gap-4 border-l-2 border-edge pl-4 sm:ml-[46px]"
        >
          {replies.map((reply) => (
            <CommentCard
              key={reply.id}
              comment={reply}
              depth={1}
              canParticipate={canParticipate}
              mostUsefulId={mostUsefulId}
              onVote={onVote}
              onFlag={onFlag}
              onEdit={onEdit}
              now={now}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
